import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { cn } from "./cn";

/* ---- date helpers (ISO 'YYYY-MM-DD', local) ---- */
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmt = (s) =>
  s ? parse(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function DateRange({ value = {}, onChange, label, placeholder = "Select range", className }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from || null);
  const [to, setTo] = useState(value.to || null);
  const [view, setView] = useState(() => parse(value.to || value.from) || new Date());
  const ref = useRef(null);

  useEffect(() => {
    setFrom(value.from || null);
    setTo(value.to || null);
  }, [value.from, value.to]);

  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const commit = (f, t) => {
    setFrom(f);
    setTo(t);
    if (f && t) onChange?.({ from: f, to: t });
  };

  const pickDay = (d) => {
    const iso = toISO(d);
    if (!from || (from && to)) {
      commit(iso, null);
    } else {
      const f = parse(from);
      if (d < f) commit(iso, null);
      else commit(from, iso);
    }
  };

  const applyPreset = (days, mode) => {
    const today = new Date();
    let f;
    if (mode === "month") f = new Date(today.getFullYear(), today.getMonth(), 1);
    else f = addDays(today, -(days - 1));
    const range = { from: toISO(f), to: toISO(today) };
    setFrom(range.from);
    setTo(range.to);
    setView(today);
    onChange?.(range);
    setOpen(false);
  };

  /* calendar grid (Monday-first) */
  const y = view.getFullYear();
  const m = view.getMonth();
  const firstWeekday = (new Date(y, m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(y, m, i + 1))];

  const fromD = parse(from);
  const toD = parse(to);

  const presets = [
    { label: "Today", days: 1 },
    { label: "Last 7 days", days: 7 },
    { label: "Last 30 days", days: 30 },
    { label: "Last 90 days", days: 90 },
    { label: "This month", days: 0, mode: "month" },
  ];

  return (
    <div className={cn("w-full", className)} ref={ref}>
      {label && <label className="mb-1.5 block text-start text-sm font-medium text-ink/90">{label}</label>}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-line bg-surface-2 px-3.5 text-sm outline-none transition",
            "focus:border-accent focus:ring-2 focus:ring-accent/30",
            open && "border-accent ring-2 ring-accent/30"
          )}
        >
          <span className={cn("truncate text-start", from && to ? "text-ink" : "text-faint")}>
            {from && to ? `${fmt(from)} → ${fmt(to)}` : placeholder}
          </span>
          <CalendarBlank size={17} className="shrink-0 text-faint" />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 z-50 mt-2 flex overflow-hidden rounded-2xl border border-line bg-surface shadow-pop"
            >
              {/* presets */}
              <div className="hidden w-36 flex-col gap-1 border-r border-line p-2 sm:flex">
                {presets.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => applyPreset(p.days, p.mode)}
                    className="rounded-lg px-3 py-2 text-start text-sm text-muted transition hover:bg-surface-2 hover:text-ink"
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* calendar */}
              <div className="w-[18rem] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setView(new Date(y, m - 1, 1))}
                    className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2"
                  >
                    <CaretLeft size={16} />
                  </button>
                  <span className="text-sm font-semibold text-ink">
                    {MONTHS[m]} {y}
                  </span>
                  <button
                    type="button"
                    onClick={() => setView(new Date(y, m + 1, 1))}
                    className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2"
                  >
                    <CaretRight size={16} />
                  </button>
                </div>

                <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-medium text-faint">
                  {WEEKDAYS.map((w) => (
                    <span key={w} className="py-1">{w}</span>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-y-1">
                  {cells.map((d, i) => {
                    if (!d) return <span key={`e${i}`} />;
                    const isFrom = sameDay(d, fromD);
                    const isTo = sameDay(d, toD);
                    const inRange = fromD && toD && d > fromD && d < toD;
                    const edge = isFrom || isTo;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => pickDay(d)}
                        className={cn(
                          "mx-auto grid h-9 w-9 place-items-center rounded-lg text-sm transition",
                          !edge && !inRange && "text-ink hover:bg-surface-2",
                          inRange && "bg-accent-soft text-accent-strong",
                          edge && "bg-accent text-accent-fg font-semibold"
                        )}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
